import { ORY_MESSAGE_ID_TO_KEY, type OryMessageKey } from '../i18n';

/**
 * Reader utility for Ory message properties
 *
 * Provides type-safe access to message metadata based on Ory's numeric message IDs.
 * Encapsulates the message ID to key mapping as an implementation detail.
 *
 * @example
 * ```typescript
 * // Get message key for a message
 * const key = OryMessageReader.getMessageKey(4000006);
 * // Returns: "CREDENTIALS_INVALID" | undefined
 *
 * // Use with UiText messages
 * const messages = OryFlowReader.getMessages(flow, { types: ['error'] });
 * messages.forEach(msg => {
 *   const key = OryMessageReader.getMessageKey(msg.id);
 *   if (key) {
 *     // Use key for translation, logging, etc...
 *   }
 * });
 * ```
 */
export class OryMessageReader {
  /**
   * Get message key for Ory message ID
   *
   * Maps Ory's numeric message IDs to semantic key identifiers
   * (e.g., 4000006 → "CREDENTIALS_INVALID").
   *
   * @param messageId - Numeric message ID from Ory flow/error response
   * @returns Message key in CAPSLOCK format, or undefined if ID is unknown
   *
   * @example
   * ```typescript
   * const key = OryMessageReader.getMessageKey(4000006);
   * // Returns: "CREDENTIALS_INVALID"
   *
   * const unknownKey = OryMessageReader.getMessageKey(9999999);
   * // Returns: undefined
   * ```
   */
  static getMessageKey(messageId: number): OryMessageKey | undefined {
    return ORY_MESSAGE_ID_TO_KEY[messageId];
  }
}

